import {
  type HttpValidationError,
  type PrintSquawkCards,
  type PrintSquawkList,
  type SquawkDetailPublic,
  SquawkPrintingService,
  type SquawkQuotePublic,
  type SquawkSort,
  type SquawkStatusUpdatesPublic,
  type SquawkSuggestionsPublic,
  type SquawksPublic,
  SquawksService,
  type SquawkUpdate,
} from "@common/client"
import {
  type InfiniteData,
  type QueryClient,
  type QueryKey,
  useIsMutating,
  useMutation,
  useMutationState,
  useQueryClient,
} from "@tanstack/react-query"
import type { AxiosError } from "axios"
import {
  makeAxiosMutation,
  makeGroupPages,
  makeInfiniteQuery,
  makeQuery,
  parseBlobErrors,
  unwrap,
} from "../utils"
import {
  isPredictableSquawkUpdate,
  squawkWithUpdate,
} from "./optimistic-squawk"
import {
  quoteRefetchKeys,
  quotesBaseKey,
  useClassifyTaskGroupsMutation,
} from "./quote"

export const squawksBaseKey = ["squawks"] as const

export const squawksListKey = (
  params: Parameters<typeof SquawksService.listSquawks>[0],
) => [...squawksBaseKey, params] as const

export const squawksQuery = makeQuery({
  serviceCall: SquawksService.listSquawks,
  queryKey: squawksListKey,
})

export const squawkFilterOptionsQuery = makeQuery({
  serviceCall: SquawksService.filterOptions,
  queryKey: (params) => [...squawksBaseKey, "filter-options", params],
})

const SQUAWKS_PAGE_SIZE = 50

export type SquawkPagesParams = Omit<
  Parameters<typeof SquawksService.listSquawks>[0],
  "skip" | "limit"
>

export const squawksInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: SquawkPagesParams, page) =>
    SquawksService.listSquawks({ ...params, ...page }),
  queryKey: (params) => [...squawksBaseKey, "infinite", params],
  pageSize: SQUAWKS_PAGE_SIZE,
})

export const squawkGroupPages = makeGroupPages({
  serviceCall: (params: SquawkPagesParams, page) =>
    SquawksService.listSquawks({ ...params, ...page }),
  queryKey: (params) => [...squawksBaseKey, "grouped", params],
  pageSize: SQUAWKS_PAGE_SIZE,
})

export const squawkKey = (squawk_id: string) =>
  [...squawksBaseKey, squawk_id] as const

export const squawkQuery = makeQuery({
  serviceCall: SquawksService.get,
  queryKey: ({ squawk_id }) => squawkKey(squawk_id),
})

export const squawkSuggestionsKey = (squawk_id: string) =>
  ["squawk-suggestions", squawk_id] as const

export const squawkSuggestionsQuery = makeQuery({
  serviceCall: SquawksService.suggestDetails,
  queryKey: ({ squawk_id }) => squawkSuggestionsKey(squawk_id),
  staleTime: Number.POSITIVE_INFINITY,
})

export const squawkDuplicatesKey = (squawk_id: string) =>
  ["squawk-duplicates", squawk_id] as const

export const squawkDuplicatesQuery = makeQuery({
  serviceCall: SquawksService.findDuplicates,
  queryKey: ({ squawk_id }) => squawkDuplicatesKey(squawk_id),
  staleTime: Number.POSITIVE_INFINITY,
})

export const squawkCommentsKey = (squawk_id: string) =>
  ["squawk-comments", squawk_id] as const

export const squawkCommentsQuery = makeQuery({
  serviceCall: SquawksService.listComments,
  queryKey: (params) => [...squawkCommentsKey(params.squawk_id), params],
})

export const squawkApprovalHistoryBaseKey = ["squawk-approval-history"] as const

export const squawkApprovalHistoryKey = (squawk_id: string) =>
  [...squawkApprovalHistoryBaseKey, squawk_id] as const

export const squawkApprovalHistoryQuery = makeQuery({
  serviceCall: SquawksService.approvalHistory,
  queryKey: ({ squawk_id }) => squawkApprovalHistoryKey(squawk_id),
})

export const useCreateSquawkMutation = makeAxiosMutation({
  serviceCall: SquawksService.create,
  invalidates: () => [squawksBaseKey],
})

export const useCreateSquawkCommentMutation = makeAxiosMutation({
  serviceCall: SquawksService.addComment,
  invalidates: (params) => [squawkCommentsKey(params.squawk_id)],
})

type QuoteSquawksInput = Parameters<typeof SquawksService.quoteSquawks>[0]

export const useQuoteSquawksMutation = () => {
  const queryClient = useQueryClient()
  const classify = useClassifyTaskGroupsMutation()

  return useMutation<
    SquawkQuotePublic,
    AxiosError<HttpValidationError>,
    QuoteSquawksInput
  >({
    mutationFn: async (input) => {
      const data = await SquawksService.quoteSquawks(input)
      return unwrap(data)
    },
    onSuccess: (quoted) => {
      const taskIds = quoted.squawks.flatMap((squawk) =>
        squawk.quote_task_id ? [squawk.quote_task_id] : [],
      )
      if (taskIds.length > 0) {
        classify.mutate({ classifyTasks: { task_ids: taskIds } })
      }
      for (const queryKey of [
        squawksBaseKey,
        ...quoteRefetchKeys(quoted.quote.id),
      ]) {
        void queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}

export const squawkLabourKey = (squawk_id: string) =>
  ["squawk-labour", squawk_id] as const

export const squawkLabourQuery = makeQuery({
  serviceCall: SquawksService.estimateLabour,
  staleTime: 5 * 60 * 1000,
  queryKey: ({ squawk_id, aircraft_type_ids, same_aircraft_type }) => [
    ...squawkLabourKey(squawk_id),
    aircraft_type_ids ?? [],
    same_aircraft_type ?? true,
  ],
})

export const squawkPastPartsBaseKey = ["squawk-past-parts"] as const

export const squawkPastPartsKey = (squawk_id: string) =>
  [...squawkPastPartsBaseKey, squawk_id] as const

export const squawkPastPartsQuery = makeQuery({
  serviceCall: SquawksService.listPastParts,
  queryKey: ({ squawk_id }) => squawkPastPartsKey(squawk_id),
})

export const squawkPriceHistoryBaseKey = ["squawk-price-history"] as const

export const squawkPriceHistoryKey = (squawk_id: string) =>
  [...squawkPriceHistoryBaseKey, squawk_id] as const

export const squawkPriceHistoryQuery = makeQuery({
  serviceCall: SquawksService.getPriceHistory,
  queryKey: ({ squawk_id }) => squawkPriceHistoryKey(squawk_id),
})

export const squawkAvrefPricesBaseKey = ["squawk-avref-prices"] as const

export const squawkAvrefPricesKey = (charge_id: string) =>
  [...squawkAvrefPricesBaseKey, charge_id] as const

export const squawkAvrefPricesQuery = makeQuery({
  serviceCall: SquawksService.searchAvrefPrices,
  queryKey: ({ charge_id }) => squawkAvrefPricesKey(charge_id),
})

type UpdateSquawkInput = Parameters<typeof SquawksService.update>[0]

export const isSquawkListKey = (queryKey: QueryKey): boolean =>
  queryKey[0] === squawksBaseKey[0] &&
  queryKey.length === 2 &&
  typeof queryKey[1] === "object"

const isSquawkPagesKey = (queryKey: QueryKey): boolean =>
  queryKey[0] === squawksBaseKey[0] && queryKey[1] === "infinite"

const isSquawkGroupedKey = (queryKey: QueryKey): boolean =>
  queryKey[0] === squawksBaseKey[0] && queryKey[1] === "grouped"

const isSquawkPageKey = (queryKey: QueryKey): boolean =>
  isSquawkListKey(queryKey) || isSquawkGroupedKey(queryKey)

const isListingKey = (queryKey: QueryKey): boolean =>
  isSquawkPageKey(queryKey) || isSquawkPagesKey(queryKey)

const isFilterOptionsKey = (queryKey: QueryKey): boolean =>
  queryKey[0] === squawksBaseKey[0] && queryKey[1] === "filter-options"

export function updateListedSquawks(
  queryClient: QueryClient,
  update: (squawk: SquawkDetailPublic) => SquawkDetailPublic,
): void {
  queryClient.setQueriesData<SquawksPublic>(
    { predicate: (query) => isSquawkPageKey(query.queryKey) },
    (list) => list && { ...list, data: list.data.map(update) },
  )
  queryClient.setQueriesData<InfiniteData<SquawksPublic>>(
    { predicate: (query) => isSquawkPagesKey(query.queryKey) },
    (pages) =>
      pages && {
        ...pages,
        pages: pages.pages.map((page) => ({
          ...page,
          data: page.data.map(update),
        })),
      },
  )
}

function listedSquawks(queryClient: QueryClient): SquawkDetailPublic[] {
  const lists = queryClient.getQueriesData<SquawksPublic>({
    predicate: (query) => isSquawkPageKey(query.queryKey),
  })
  const pages = queryClient.getQueriesData<InfiniteData<SquawksPublic>>({
    predicate: (query) => isSquawkPagesKey(query.queryKey),
  })
  return [
    ...lists.flatMap(([, list]) => list?.data ?? []),
    ...pages.flatMap(
      ([, infinite]) => infinite?.pages.flatMap((page) => page.data) ?? [],
    ),
  ]
}

function patchSquawkLists(
  queryClient: QueryClient,
  updated: SquawkDetailPublic,
): void {
  updateListedSquawks(queryClient, (squawk) =>
    squawk.id === updated.id ? updated : squawk,
  )
}

function onSquawkUpdated(
  queryClient: QueryClient,
  squawk: SquawkDetailPublic,
): Promise<void> {
  queryClient.setQueryData(squawkKey(squawk.id), squawk)
  patchSquawkLists(queryClient, squawk)
  return queryClient.invalidateQueries({ queryKey: quotesBaseKey })
}

const REGROUPING_FIELDS: ReadonlySet<string> = new Set<keyof SquawkUpdate>([
  "status",
  "severity",
  "work_package_number",
  "work_package_id",
  "work_task_id",
  "reported_by_user_id",
  "ata_chapter",
])

const SEARCHED_FIELDS: ReadonlySet<string> = new Set<keyof SquawkUpdate>([
  "number",
  "description",
  "manual_reference",
  "resolution",
])

const SORTED_FIELDS: ReadonlyMap<string, SquawkSort> = new Map<
  keyof SquawkUpdate,
  SquawkSort
>([
  ["number", "number"],
  ["description", "squawk"],
  ["charges", "value"],
])

function listedBy(queryKey: QueryKey): { sort: unknown; search: unknown } {
  const params = queryKey.find(
    (part): part is object => typeof part === "object" && part !== null,
  )
  if (!params) {
    return { sort: undefined, search: undefined }
  }
  return {
    sort: "sort" in params ? params.sort : undefined,
    search: "search" in params ? params.search : undefined,
  }
}

function movesListing(fields: string[], queryKey: QueryKey): boolean {
  const { sort, search } = listedBy(queryKey)
  return fields.some(
    (field) =>
      REGROUPING_FIELDS.has(field) ||
      (Boolean(search) && SEARCHED_FIELDS.has(field)) ||
      (SORTED_FIELDS.has(field) && SORTED_FIELDS.get(field) === sort),
  )
}

function refetchMovedListings(
  queryClient: QueryClient,
  fields: string[],
): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      (isListingKey(query.queryKey) || isFilterOptionsKey(query.queryKey)) &&
      movesListing(fields, query.queryKey),
  })
}

const updateSquawkBaseKey = ["update-squawk"] as const

const updateSquawkScope = { id: "update-squawk" }

export const updateSquawkKey = (squawk_id: string) =>
  [...updateSquawkBaseKey, squawk_id] as const

function isUpdateInput(variables: unknown): variables is UpdateSquawkInput {
  return (
    typeof variables === "object" &&
    variables !== null &&
    "squawk_id" in variables &&
    "squawkUpdate" in variables
  )
}

function savingSquawk(queryClient: QueryClient, squawk_id: string): number {
  return queryClient
    .getMutationCache()
    .findAll({ mutationKey: updateSquawkBaseKey, status: "pending" })
    .filter(
      (mutation) =>
        isUpdateInput(mutation.state.variables) &&
        mutation.state.variables.squawk_id === squawk_id,
    ).length
}

export const useUnpredictedSquawkSave = (squawk_id?: string): boolean =>
  useMutationState({
    filters: { mutationKey: updateSquawkBaseKey, status: "pending" },
    select: (mutation) => mutation.state.variables,
  }).some(
    (variables) =>
      isUpdateInput(variables) &&
      (squawk_id === undefined || variables.squawk_id === squawk_id) &&
      !isPredictableSquawkUpdate(variables.squawkUpdate),
  )

function cachedSquawk(
  queryClient: QueryClient,
  squawk_id: string,
): SquawkDetailPublic | undefined {
  return (
    queryClient.getQueryData<SquawkDetailPublic>(squawkKey(squawk_id)) ??
    listedSquawks(queryClient).find((squawk) => squawk.id === squawk_id)
  )
}

export const useUpdateSquawkMutation = (squawk_id?: string) => {
  const queryClient = useQueryClient()

  return useMutation<
    SquawkDetailPublic,
    AxiosError<HttpValidationError>,
    UpdateSquawkInput,
    SquawkDetailPublic | undefined
  >({
    mutationKey: squawk_id ? updateSquawkKey(squawk_id) : updateSquawkBaseKey,
    scope: updateSquawkScope,
    mutationFn: async (input) => {
      const data = await SquawksService.update(input)
      return unwrap(data)
    },
    onMutate: async (input) => {
      if (!isPredictableSquawkUpdate(input.squawkUpdate)) return
      await queryClient.cancelQueries({
        queryKey: squawksBaseKey,
        predicate: (query) => query.state.data !== undefined,
      })
      const current = cachedSquawk(queryClient, input.squawk_id)
      if (!current) return
      const predicted = squawkWithUpdate(current, input.squawkUpdate)
      queryClient.setQueryData(squawkKey(predicted.id), predicted)
      patchSquawkLists(queryClient, predicted)
      return current
    },
    onSuccess: (squawk, { squawkUpdate }) => {
      if (
        squawkUpdate.description !== undefined ||
        squawkUpdate.manual_reference !== undefined ||
        squawkUpdate.work_package_number !== undefined
      ) {
        void queryClient.invalidateQueries({
          queryKey: squawkLabourKey(squawk.id),
        })
        void queryClient.invalidateQueries({
          queryKey: squawkPastPartsKey(squawk.id),
        })
        void queryClient.invalidateQueries({
          queryKey: squawkDuplicatesKey(squawk.id),
        })
      }
      if (squawkUpdate.status !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: squawkApprovalHistoryKey(squawk.id),
        })
      }
      if (squawkUpdate.charges !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: squawkPriceHistoryKey(squawk.id),
        })
        void queryClient.invalidateQueries({
          queryKey: squawkAvrefPricesBaseKey,
        })
      }
      void refetchMovedListings(
        queryClient,
        Object.entries(squawkUpdate).flatMap(([field, value]) =>
          value === undefined ? [] : [field],
        ),
      )
      const othersSaving = savingSquawk(queryClient, squawk.id) > 1
      return othersSaving
        ? queryClient.invalidateQueries({ queryKey: quotesBaseKey })
        : onSquawkUpdated(queryClient, squawk)
    },
    onError: (_error, _input, previous) => {
      if (previous) {
        queryClient.setQueryData(squawkKey(previous.id), previous)
        patchSquawkLists(queryClient, previous)
      }
      void queryClient.invalidateQueries({ queryKey: squawksBaseKey })
    },
  })
}

type UpdateSquawkStatusesInput = Parameters<
  typeof SquawksService.updateStatuses
>[0]

export const useUpdateSquawkStatusesMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    SquawkStatusUpdatesPublic,
    AxiosError<HttpValidationError>,
    UpdateSquawkStatusesInput
  >({
    scope: updateSquawkScope,
    mutationFn: async (input) => {
      const data = await SquawksService.updateStatuses(input)
      return unwrap(data)
    },
    onSuccess: ({ updated }) => {
      const byId = new Map(updated.map((squawk) => [squawk.id, squawk]))
      for (const squawk of updated) {
        queryClient.setQueryData(squawkKey(squawk.id), squawk)
      }
      updateListedSquawks(
        queryClient,
        (squawk) => byId.get(squawk.id) ?? squawk,
      )
      void refetchMovedListings(queryClient, ["status"])
      void queryClient.invalidateQueries({
        queryKey: squawkApprovalHistoryBaseKey,
      })
      return queryClient.invalidateQueries({ queryKey: quotesBaseKey })
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: squawksBaseKey })
    },
  })
}

type ApplySquawkSuggestionInput = Parameters<
  typeof SquawksService.applySuggestion
>[0]

export const applySquawkSuggestionKey = (squawk_id: string) =>
  ["apply-squawk-suggestion", squawk_id] as const

export const useApplySquawkSuggestionMutation = (squawk_id: string) => {
  const queryClient = useQueryClient()

  return useMutation<
    SquawkDetailPublic,
    AxiosError<HttpValidationError>,
    ApplySquawkSuggestionInput
  >({
    mutationKey: applySquawkSuggestionKey(squawk_id),
    scope: updateSquawkScope,
    mutationFn: async (input) => {
      const data = await SquawksService.applySuggestion(input)
      return unwrap(data)
    },
    onSuccess: (squawk, { applySquawkSuggestion: { question } }) => {
      queryClient.setQueryData<SquawkSuggestionsPublic>(
        squawkSuggestionsKey(squawk.id),
        (current) =>
          current && {
            suggestions: current.suggestions.filter(
              (suggestion) => suggestion.question !== question,
            ),
          },
      )
      void queryClient.invalidateQueries({
        queryKey: squawkDuplicatesKey(squawk.id),
      })
      return onSquawkUpdated(queryClient, squawk)
    },
  })
}

type SetSquawkAvrefPriceInput = Parameters<
  typeof SquawksService.setAvrefPrice
>[0]

const setSquawkAvrefPriceBaseKey = ["set-squawk-avref-price"] as const

export const setSquawkAvrefPriceKey = (squawk_id: string) =>
  [...setSquawkAvrefPriceBaseKey, squawk_id] as const

export const useSquawkAvrefPricing = (squawk_id?: string): boolean =>
  useIsMutating({
    mutationKey: squawk_id
      ? setSquawkAvrefPriceKey(squawk_id)
      : setSquawkAvrefPriceBaseKey,
  }) > 0

export const useSetSquawkAvrefPriceMutation = (squawk_id: string) => {
  const queryClient = useQueryClient()

  return useMutation<
    SquawkDetailPublic,
    AxiosError<HttpValidationError>,
    SetSquawkAvrefPriceInput
  >({
    mutationKey: setSquawkAvrefPriceKey(squawk_id),
    scope: updateSquawkScope,
    mutationFn: async (input) => {
      const data = await SquawksService.setAvrefPrice(input)
      return unwrap(data)
    },
    onSuccess: (squawk) => {
      void queryClient.invalidateQueries({
        queryKey: squawkPriceHistoryKey(squawk.id),
      })
      void refetchMovedListings(queryClient, ["charges"])
      return savingSquawk(queryClient, squawk.id) > 0
        ? queryClient.invalidateQueries({ queryKey: quotesBaseKey })
        : onSquawkUpdated(queryClient, squawk)
    },
  })
}

export const useArchiveSquawkMutation = makeAxiosMutation({
  serviceCall: SquawksService.archive,
  invalidates: () => [squawksBaseKey, quotesBaseKey],
})

export const useRestoreSquawkMutation = makeAxiosMutation({
  serviceCall: SquawksService.restore,
  invalidates: () => [squawksBaseKey],
})

export const useDeleteSquawkMutation = makeAxiosMutation({
  serviceCall: SquawksService.delete,
  invalidates: () => [squawksBaseKey, quotesBaseKey],
})

export const useArchiveSquawksMutation = makeAxiosMutation({
  serviceCall: SquawksService.archiveMany,
  invalidates: () => [squawksBaseKey, quotesBaseKey],
})

export const useRestoreSquawksMutation = makeAxiosMutation({
  serviceCall: SquawksService.restoreMany,
  invalidates: () => [squawksBaseKey],
})

export const usePrintSquawkCardsMutation = makeAxiosMutation({
  serviceCall: parseBlobErrors(
    (params: { printSquawkCards: PrintSquawkCards }) =>
      SquawkPrintingService.printSquawkCards(params),
  ),
  invalidates: () => [],
})

export const usePrintSquawkListMutation = makeAxiosMutation({
  serviceCall: parseBlobErrors((params: { printSquawkList: PrintSquawkList }) =>
    SquawkPrintingService.printSquawkList(params),
  ),
  invalidates: () => [],
})
